/* global klaviyo */
/**
 *
 *  TrainerProfilePage
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 28/02/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { Dimensions } from "react-native";
import { useHistory, useParams, useLocation, Link } from "react-router-dom";
// import CSSTransitionGroup from "react-transition-group/CSSTransitionGroup";
import Transition, {
  ENTERING,
  ENTERED, // eslint-disable-line
  EXITING,
  EXITED,
} from "react-transition-group/Transition";
import moment from "moment-timezone";
import Modal from "react-modal";
import Slider from "react-slick";
import Carousel from "react-gallery-carousel";
import Cookies from "js-cookie";
import Lottie from "react-lottie";
import { FormattedMessage } from "react-intl";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";
import { useSignUpState } from "../../state/SignUpState";
import { useRegion } from "../../context/RegionProvider";

// Components
import Button from "../Button/Button";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import TrainerRating from "../TrainerRating/TrainerRating";
import BookingBox from "../BookingBox/BookingBox";
import PartnerProductBadge from "../PartnerProductBadge/PartnerProductBadge";
import RewardBadge from "./RewardBadge/RewardBadge";
import ShareModal from "../ShareModal/ShareModal";
import BundlePriceBox from "../BundlePriceBox/BundlePriceBox";
import BookingFlowPopup from "../BookingFlow/BookingFlowPopup";
import TruBeDrawer from "../TruBeDrawer/TruBeDrawer";
import LocationSelector from "../LocationSelector/LocationSelector";
import BookingFlowPanel from "../BookingFlow/BookingFlowPanel/BookingFlowPanel";
import BookingFlowPanelHeader from "../BookingFlow/BookingFlowPanelHeader/BookingFlowPanelHeader";
import { SliderNextButton } from "../SliderNextButton/SliderNextButton";
import { SliderPrevButton } from "../SliderPrevButton/SliderPrevButton";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import Reviews from "../Reviews/Reviews";
import ReviewsHeader from "../Reviews/ReviewsHeader/ReviewsHeader";
import ClampLines from "../ClampLines/ClampLines";
import SignUpForm from "../SignUp/SignUpForm";
import WhatsAppLink from "../WhatsAppLink/WhatsAppLink";
import PhoneNumberLink from "../PhoneNumberLink/PhoneNumberLink";
import { MarkdownRenderer } from "../MarkdownRenderer/MarkdownRenderer";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";
import useBodyScrollLock from "../../hooks/useBodyScrollLock/useBodyScrollLock";

// Icons
import CalendarIcon from "../../icons/CalendarTruBeIcon";
import CaretRightIcon from "../../icons/CaretRightIcon";
import ContactIcon from "../../icons/ContactIcon";
import ShareIcon from "../../icons/ShareIcon";
import ExperienceIcon from "./ExperienceIcon";
import QualificationIcon from "./QualificationIcon";
import LanguagesIcon from "./LanguagesIcon";
import MiscIcon from "./MiscIcon";
import CaretDownIcon from "../../icons/CaretDownIcon";
import CloseIcon from "../../icons/CloseIcon";
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";
import StarFullIcon from "../../icons/StarFullIcon";
// import SessionTypeOnlineIcon from "../../icons/SessionTypeOnlineIcon";
// import MapMarkerIcon from "../../icons/MapMarkerIcon";
import WeightIcon from "../../icons/WeightIcon";

// Utils
import {
  clientWidth,
  getCountryInfo,
  getLastUsedLocation,
  getPartnerPictureUrl,
  isAndroidDevice,
  isIosDevice,
  isMac,
  isMobile,
  toQueryString,
} from "../../utils";
import { getMoment, getMomentWithTz } from "../../utils/date";
import { modalStyles, PAGE_SIZE } from "../../constants";

// Images
import defaultPartnerPhoto from "../../images/generic-trainerphoto.svg";

import { areas } from "../../california-zip-codes";
import animationData from "../../lotties/loading-spinner";

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

const OFFSET = 127397;
const CONTINUE_BOOKING_FLOW_PARAM_NAME = "continue";

const reviewsSliderResponsiveSettings = [
  {
    breakpoint: 640,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 1600,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 5000,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
];

const { height } = Dimensions.get("window");

export const ReadMore = ({
  text,
  position,
  gradientCn = "tbk-to-grey-light lg:tbk-to-basic-white",
}) => {
  const { theme } = useTheme();

  const [showMore, setShowMore] = useState(
    false
    //clientWidth() >= theme.responsive.breakpoints.lg
  );

  const maxText = clientWidth() >= theme.responsive.breakpoints.lg ? 868 : 280;
  const height = clientWidth() >= theme.responsive.breakpoints.lg ? 256 : 184;

  // let lessText = text.slice(0, maxText);

  const handleReadMoreClick = useCallback(() => {
    setShowMore(true);
  }, []);

  return (
    <div className="tbk-relative">
      <div
        className="tbk-overflow-hidden"
        style={{
          height: !showMore && text.length > maxText ? `${height}px` : null,
        }}
      >
        {text && !showMore ? (
          <MarkdownRenderer
            className="tbk-text-main tbk-mb-2 tbk-text-primary"
            markdown={text.slice(0, maxText)}
          />
        ) : (
          <MarkdownRenderer
            className="tbk-text-main tbk-mb-2 tbk-text-primary"
            markdown={text}
          />
        )}
      </div>
      {!showMore && text.length > maxText && (
        <>
          <div
            className={`tbk-pointer-events-none tbk-absolute tbk-bottom-5 tbk-left-0 tbk-right-0 tbk-h-8 tbk-w-full tbk-bg-gradient-to-b tbk-from-transparent ${gradientCn} lg:tbk-h-36 `}
          />
          <div
            className={`tbk-mt-2 ${
              position === "right" ? "tbk-text-right md:tbk-text-left" : ""
            }`}
          >
            <button
              className="tb-session-link tbk-text-main tbk-inline-flex tbk-text-blue"
              onClick={handleReadMoreClick}
            >
              Read More
              <CaretDownIcon />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const MediaBlock = ({ media, initialSlide }) => {
  // const images = [
  //     {
  //         src: `https://www.youtube.com/watch?v=wAOyxWe8Lz8`
  //     },
  //     {
  //         src: `https://www.youtube.com/watch?v=6hMajEGPbGo`
  //     },
  //     {
  //         src: `${mediaBlockImg1}`
  //     },
  //     {
  //         src: `${mediaBlockImg2}`
  //     },
  //     {
  //         src: `${mediaBlockImg3}`
  //     },
  //     {
  //         src: `${mediaBlockImg4}`
  //     },
  //     {
  //         src: `${mediaBlockImg5}`
  //     }
  // ];

  const mediaContent = media;

  return (
    <Carousel
      // images={images}
      hasIndexBoard={false}
      hasMediaButton={false}
      hasSizeButton={false}
      hasThumbnails={false}
      index={initialSlide}
      leftIcon={
        <svg
          width="8"
          height="13"
          viewBox="0 0 8 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8 1.5L6.5 0L0 6.5L6.5 13L8 11.5L3 6.5L8 1.5Z"
            fill="#CBCFD5"
          />
        </svg>
      }
      rightIcon={
        <svg
          width="8"
          height="13"
          viewBox="0 0 8 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0 1.5L1.5 0L8 6.5L1.5 13L0 11.5L5 6.5L0 1.5Z"
            fill="#CBCFD5"
          />
        </svg>
      }
      style={{ height: "100%", width: "100%" }}
      onIndexChange={({ curIndex, curIndexForDisplay }) => {
        const ytPlayerIframes = document.querySelectorAll(".yt_player_iframe");

        [].forEach.call(ytPlayerIframes, function (iframe) {
          iframe.contentWindow.postMessage(
            '{"event":"command","func":"stopVideo","args":""}',
            "*"
          );
        });
      }}
    >
      {mediaContent &&
        mediaContent.map((block) => {
          const url = new URL(block.url);
          const query = new URLSearchParams(block.url);

          url.search = "";

          query.append("enablejsapi", 1);

          return block.type === "VIDEO" ? (
            <iframe
              class="yt_player_iframe"
              width={(height * 16) / 9}
              height={height - 128}
              src={`${url.toString()}?${query.toString()}`}
            ></iframe>
          ) : (
            <figure className="_3iVQ0">
              <img className="_t1897" src={block.url} />
            </figure>
          );
        })}
    </Carousel>
  );
};

export default function TrainerProfilePage() {
  const history = useHistory();
  const params = useParams();
  const location = useLocation();

  const { theme } = useTheme();
  const { region } = useRegion();

  const {
    user,
    hasTriedToAutoLogin,
    partnerSummary,
    prices: partnerPrices,
    partners: availablePartners,
    partnerReviews,
    partnerReviewsNumber,
    referralCode,
    setReferralCode,
    applyReferral,
    loadPartnersInLocation: loadPartners,
    loadPartnerProfile,
    loadPartnerAvailability,
    loadPrices,
    loadReviews,
    loadGyms,
    loadPartnerGyms,
    partnerLoaded,
    partnerAvailability,
    partnerAvailabilityLoaded,
    partnerGyms,
    isFetching,
    clearPartnerSummaryReceive,
    clearAvailablePartnersLoaded,
    clearPartnerAvailabilityLoaded,
    clearPrices,
    clearPartnerReviews,
    clearPartnerReviewsLoaded,
  } = useAppState();

  const { userPhoneCode, userPhoneNumber, setAuthState } = useSignUpState();

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLocationSelectorModalOpen, setIsLocationSelectorModalOpen] =
    useState(false);
  const [minTime] = useState(getMoment().startOf("day").add(330, "minute"));
  const [maxTime] = useState(
    minTime.clone().endOf("day").hour(23).add(3, "month")
  );
  const [isShareModalOpen, setIsShareModalOpen] = useState();
  const [experience, setExperience] = useState(0);
  const [isBookingFlowPopupOpen, setIsBookingFlowPopupOpen] = useState(false);
  const [isBookingFlowPanelOpen, setIsBookingFlowPanelOpen] = useState(false);
  const [isBookingFlowPanelFullyOpen, setIsBookingFlowPanelFullyOpen] =
    useState(false);
  // eslint-disable-next-line
  const [sessionType, setSessionType] = useState("OFFLINE");
  const [bookingStep, setBookingStep] = useState("CHOOSE_DAY_TIME");
  const [workoutType, setWorkoutType] = useState();
  const [_bookingTime, setBookingTime] = useState();
  const [startTime, setStartTime] = useState();
  const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false);
  const [showSpecifyLocationInfo, setShowSpecifyLocationInfo] = useState(false);
  const [showMoreSpecialisations, setShowMoreSpecialisations] = useState(false);
  const [showAllGyms, setShowAllGyms] = useState(false); // TODO make more robust
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [bookingFlowContinue, setBookingFlowContinue] = useState(null);
  const [isGalleryCarouselModalOpen, setIsGalleryCarouselModalOpen] =
    useState(null);
  const [galleryInitialSlide, setGalleryInitialSlide] = useState(0);
  const [partnerMedia, setPartnerMedia] = useState([]);
  const [autoPlay, setAutoPlay] = useState(true);

  const panelRef = useRef(null);
  const bookingFlowPopupRef = useRef(null);
  const bookingFlowPanelRef = useRef(null);
  const locationSelectorResolveRef = useRef(null);
  const reviewsModalResolveRef = useRef(null);
  const getAvailablePartnersCancelablePromiseRef = useRef();
  const availablePartnersRef = useRef();
  const selectedLocationRef = useRef(null);

  const defaultCustomStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? Object.assign(
          {},
          { ...modalStyles.mobileDefault },
          {
            content: {
              ...modalStyles.mobileDefault.content,
              bottom: "unset",
              padding: "24px",
            },
            overlay: {
              ...modalStyles.mobileDefault.overlay,
              zIndex: 1060,
            },
          }
        )
      : Object.assign(
          {},
          { ...modalStyles.default },
          { overlay: { ...modalStyles.default.overlay, zIndex: 1060 } }
        );

  const locationSelectorModalCustomStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? Object.assign(
          {},
          { ...modalStyles.mobileDefault },
          {
            content: {
              ...modalStyles.mobileDefault.content,
              display: "flex",
              minHeight: showAllGyms ? null : "764px", // "642px",
              padding: "16px",
              overflow: showAllGyms ? "hidden" : "visible",
            },
          }
        )
      : clientWidth() < theme.responsive.breakpoints.md
      ? Object.assign(
          {},
          { ...modalStyles.reviewsModal },
          {
            content: {
              ...modalStyles.reviewsModal.content,
              width: "80%",
              height: "619px", // "auto",
              // minHeight: "490px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            },
          }
        )
      : Object.assign(
          {},
          { ...modalStyles.reviewsModal },
          {
            content: {
              ...modalStyles.reviewsModal.content,
              width: "800px",
              height: "619px", // "auto",
              // minHeight: "490px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            },
          }
        ); // <= 576?

  const reviewsModalCustomStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? Object.assign(
          {},
          { ...modalStyles.newMobileDefault },
          {
            content: {
              ...modalStyles.newMobileDefault.content,
              // height: "473px",
              // minHeight: "436px",
              // top: "auto",
            },
          }
        )
      : Object.assign(
          {},
          { ...modalStyles.reviewsModal },
          {
            content: {
              ...modalStyles.reviewsModal.content,
              top: "auto",
              bottom: "auto",
              width: "850px",
              //height: "450px",
              minHeight: "200px",
              height: "calc(100vh - 112px)",

              padding: 0,
            },
          }
        );

  const isOnline = false;

  const numberOfReviewsToShow =
    partnerReviews && partnerReviews.filter((v) => v.review).length;
  const showReviewsSlider = !!numberOfReviewsToShow;

  useEffect(
    () => {
      Promise.all([
        loadPartnerProfile(params.Id).promise,
        loadPrices(params.Id, user ? user.region : region),
      ]).then((resp) => {
        const partnerPrice =
          resp[1] &&
          resp[1].data &&
          resp[1].data.length &&
          resp[1].data.filter((v) => v.metadata.type === "BOOKING");

        // Only for production
        if (typeof klaviyo !== "undefined") {
          var item = {
            ProductName: `${resp[0].firstName} ${resp[0].lastName}`,
            ProductID: resp[0].id,
            SKU: `${resp[0].firstName.toUpperCase()}${resp[0].lastName.toUpperCase()}`,
            Categories: resp[0].products.map((v) => v.name),
            ImageURL: resp[0].photo,
            URL: window.location.href,
            Brand: "TruBe",
            Price:
              partnerPrice && partnerPrice.length
                ? partnerPrice[0].price.amount
                : "",
            CompareAtPrice: 30,
          };

          klaviyo.push(["track", "Viewed Product", item]);
        }
      });

      // TODO make more robust
      // const storageKey = `${
      //   user ? user.firstName : "default"
      // }/last-used-location`;
      //
      // const lastUsedLocation = JSON.parse(localStorage.getItem(storageKey));

      const lastUsedLocation = getLastUsedLocation(user);

      if (lastUsedLocation) {
        setSelectedLocation(lastUsedLocation);
        selectedLocationRef.current = lastUsedLocation;
        // } else {
        //   openLocationSelectorModal();
      }

      const query = new URLSearchParams(location.search),
        startTime = query.get("start_time");

      if (startTime) {
        setBookingTime(Number(startTime));
      }

      const referralCode = Cookies.get("referral");

      if (referralCode) {
        setReferralCode(referralCode);
      }

      return () => {
        clearPartnerSummaryReceive();
        // clearAvailablePartnersLoaded();
        clearPrices();
        clearPartnerReviews();
        clearPartnerReviewsLoaded();
      };
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (!hasTriedToAutoLogin) {
      return;
    }

    loadGyms({
      region: user ? user.region : region /*process.env.REACT_APP_REGION*/,
    }); // :>
  }, [user, hasTriedToAutoLogin]);

  useEffect(() => {
    if (user && referralCode) {
      applyReferral(user.id, referralCode)
        // .then((res) => {
        //   // Referral code is valid
        //   openInfoModal(
        //     `You received ${res.data.symbol}${res.data.receiverReward} referral credits.`,
        //     "Referral Bonus Applied"
        //   );
        // })
        // .catch((res) => {
        //   openInfoModal(res.message, "Link Error");
        // })
        .finally(() => {
          // Regardless the referral code is valid or not we don't need it anymore
          // (so it won't be applied on re-login for example)
          setReferralCode("");

          Cookies.remove("referral");
        });
    }
  }, [user, referralCode]);

  useEffect(() => {
    if (partnerLoaded && partnerSummary) {
      setExperience(
        getMoment().diff(new Date(partnerSummary.experience), "years")
      );

      loadReviews(partnerSummary.id /*, 0*/);

      loadPartnerGyms(partnerSummary.id);
    }
  }, [partnerSummary, partnerLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const getAvailablePartners = useCallback(() => {
    var params = {};

    // Location is mandatory
    if (!selectedLocation) {
      return;
    }

    if (selectedLocation && selectedLocation.postalCode) {
      params.locationCode = `${selectedLocation.postalCode.replace(/ .*/, "")}`;
    } else if (selectedLocation.placeId) {
      params.placeValue = selectedLocation.value.place;
    } else if (
      selectedLocation &&
      selectedLocation.value &&
      selectedLocation.value.place_id
    ) {
      params.locationCode = areas
        .filter((val) => val.placeId === selectedLocation.value.place_id)
        .map((val) => `${val.zipCode}`);
    }

    var result = loadPartners(
      params,
      user ? user.region : region /*process.env.REACT_APP_REGION*/
    );

    result.promise.then((res) => {
      availablePartnersRef.current = res; // For promise in sendBooking after location was selected
    });

    return result;
  }, [user, hasTriedToAutoLogin, selectedLocation, loadPartners]);

  const getAvailablePartnersRef = useRef(getAvailablePartners);

  useEffect(() => {
    getAvailablePartnersRef.current = getAvailablePartners;
  }, [getAvailablePartners]);

  // Should go after setting getAvailablePartnersRef with updated callback
  useEffect(() => {
    if (!hasTriedToAutoLogin) {
      return;
    }

    getAvailablePartnersCancelablePromiseRef.current &&
      getAvailablePartnersCancelablePromiseRef.current.cancel();
    getAvailablePartnersCancelablePromiseRef.current =
      getAvailablePartnersRef.current();

    return () => {
      getAvailablePartnersCancelablePromiseRef.current &&
        getAvailablePartnersCancelablePromiseRef.current.cancel();
    };
  }, [hasTriedToAutoLogin, selectedLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  const openLocationSelectorModal = useCallback(() => {
    // eslint-disable-next-line
    return new Promise((resolve, reject) => {
      locationSelectorResolveRef.current = resolve;

      setIsLocationSelectorModalOpen(true);

      // Analytics.trackEvent(TRAINERS_LIST_PAGE_CATEGORY, 'Open', EVENT_LOCATION_PICKER_OPEN);
    });
  }, []);

  // `closeLocationSelectorModal` callback should go before `useBodyScrollLock` hook,
  // because of `setShowAllGyms(false)` call, otherwise closing the modal will block the scroll
  // and `useBodyScrollLock` won't have a chance to "unblock" it

  /*
   * After closing the Location Selector Modal (promise resolved) the booking flow continues.
   * @param bail -- whether to skip the booking flow
   */
  const closeLocationSelectorModal = useCallback(
    (bail = false) => {
      if (partnerAvailable(partnerSummary.id, availablePartners) && !bail) {
        locationSelectorResolveRef.current(selectedLocation);
        locationSelectorResolveRef.current = null;
      } else {
        setIsLocationSelectorModalOpen(false);
        setShowAllGyms(false);

        return Promise.reject();
      }

      setIsLocationSelectorModalOpen(false);
      setShowAllGyms(false);
    },
    [selectedLocation, partnerSummary, availablePartners]
  );

  const { /*bottomBarHeight, */ refCallback, targetRef } =
    useBodyScrollLock(showAllGyms);

  const handleLocationSelect = useCallback((value) => {
    setSelectedLocation(value);
    selectedLocationRef.current = value;

    if (value && (value.postalCode || value.placeId)) {
      setShowSpecifyLocationInfo(true);
    } else {
      setShowSpecifyLocationInfo(false);
    }

    if (value === null) {
      setShowSpecifyLocationInfo(false);
    }
  }, []);

  const openShareModal = useCallback((e) => {
    setIsShareModalOpen(true);

    // Analytics.trackEvent(TRAINER_PAGE_CATEGORY, '', EVENT_SHARING_MENU);
  }, []);

  const closeShareModal = useCallback((e) => {
    setIsShareModalOpen(false);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const openReviewsModal = () => {
    return new Promise((resolve) => {
      reviewsModalResolveRef.current = resolve;

      setIsReviewsModalOpen(true);

      // Analytics.trackEvent(TRAINER_PAGE_CATEGORY, '', EVENT_SHARING_MENU);
    });
  };

  const closeReviewsModal = () => {
    if (reviewsModalResolveRef.current) {
      reviewsModalResolveRef.current();
      reviewsModalResolveRef.current = null;
    }

    setIsReviewsModalOpen(false);
  };

  const openSignUpModal = () => {
    history.replace({
      pathname: location.pathname,
      search: location.search,
      state: {
        from: `${location.pathname}${location.search}`,
      },
    });

    setIsSignUpModalOpen(true);
  };

  const closeSignUpModal = () => {
    setIsSignUpModalOpen(false);
  };

  const openGalleryCarouselModal = (index) => {
    setIsGalleryCarouselModalOpen(true);
    setGalleryInitialSlide(index);
  };

  const closeGalleryCarouselModal = () => {
    setIsGalleryCarouselModalOpen(false);
  };

  const partnerFirstName = partnerLoaded && partnerSummary.firstName;
  const partnerFamilyName = partnerLoaded && partnerSummary.lastName;

  const partnerProducts = partnerLoaded && partnerSummary.products;

  const _partnerMedia = partnerLoaded && partnerSummary.media;

  const mapMedia = async (partnerMedia) => {
    return Promise.all(
      partnerMedia.map(async (v) => {
        if (v.type === "VIDEO") {
          const resp = await fetch(
            `https://www.youtube.com/oembed?url=${encodeURIComponent(
              v.url
            )}&format=json`
          );
          const json = await resp.json();

          const re = /<iframe.+src\=(?:\"|\')(.+?)(?:\"|\')(?:.+?)\>/i;
          const match = json.html.match(re);

          if (match) {
            v.url = match[1];
          }

          console.log("found", match);
        }

        return v;
      })
    );
  };

  var mantra =
    partnerLoaded &&
    partnerSummary.mantra &&
    partnerSummary.mantra.toLowerCase() !== "false" &&
    partnerSummary.mantra.trim();

  const trainerPictureUrl =
    partnerLoaded && getPartnerPictureUrl(partnerSummary);

  const slickInitialised = true;

  const bundles =
    partnerPrices &&
    partnerPrices.length &&
    partnerPrices.filter((v) => v.metadata.type !== "BOOKING");

  const partnerPrice =
    partnerPrices &&
    partnerPrices.length &&
    partnerPrices.filter((v) => v.metadata.type === "BOOKING");

  useEffect(() => {
    const newPartnerMedia = [];

    if (_partnerMedia) {
      mapMedia(_partnerMedia).then((partnerMedia) => {
        console.log("newPartnerMedia", partnerMedia);

        newPartnerMedia.push(...partnerMedia);

        setPartnerMedia(
          newPartnerMedia.map((val, index) => ({ ...val, index }))
        );
      });
    }

    newPartnerMedia.unshift({
      type: "IMAGE",
      url: trainerPictureUrl ? trainerPictureUrl : defaultPartnerPhoto,
    });

    setPartnerMedia(newPartnerMedia.map((val, index) => ({ ...val, index })));
  }, [_partnerMedia]);

  const openTimeSlotsPopup = useCallback(() => {
    setIsBookingFlowPopupOpen(true);

    loadPartnerAvailability(partnerSummary.id); // Time slots

    setBookingStep("CHOOSE_DAY_TIME");
  }, [partnerSummary]);

  const openCheckTimeSlotsPanel = useCallback(() => {
    setIsBookingFlowPanelOpen(true);

    loadPartnerAvailability(partnerSummary.id); // Time slots

    setBookingStep("CHOOSE_DAY_TIME");
  }, [partnerSummary]);

  const handleCheckTimeSlotsClick = useCallback(
    (e) => {
      if (user) {
        openCheckTimeSlotsPanel();
      } else {
        setBookingFlowContinue("availability");

        openSignUpModal();
      }
    },
    [user, openCheckTimeSlotsPanel]
  );

  const handleTimeSlotsClick = useCallback(
    (e) => {
      if (user) {
        openTimeSlotsPopup();
      } else {
        setBookingFlowContinue("availability");

        openSignUpModal();
      }
    },
    [user, openTimeSlotsPopup]
  );

  /*
   *  PartnerSummary has been fetched in the DirectBookingForm.
   *
   *  We don't get the location with the parameters, as it's expected to be set beforehand
   *  in DirectBookingForm or BookingForm
   *
   *  params = {
   *    productId: String,
   *    startTime: Number,
   *    availablePartners: [partner.id]
   *  }
   *
   */
  const sendBooking = useCallback(() => {
    return Promise.resolve()
      .then(() => {
        if (
          !selectedLocationRef.current ||
          (selectedLocationRef.current &&
            !(
              selectedLocationRef.current.postalCode ||
              selectedLocationRef.current.placeId
            )) ||
          !partnerAvailable(partnerSummary.id, availablePartners)
        ) {
          selectedLocationRef.current && setShowSpecifyLocationInfo(true);

          closeBookingFlowPanel();

          return openLocationSelectorModal();
        }

        return Promise.resolve(selectedLocationRef.current);
        // return openLocationSelectorModal();
      })
      .then((res) => {
        // Using res as selected location when closing Location Picker
        if (
          !partnerAvailable(partnerSummary.id, availablePartnersRef.current)
        ) {
          return Promise.reject();
        }

        if (!_bookingTime || !workoutType) {
          // TODO make this check a function, as it's used down the code
          if (clientWidth() < theme.responsive.breakpoints.lg) {
            handleCheckTimeSlotsClick();
          } else {
            handleTimeSlotsClick();
          }

          return Promise.reject();
        }

        return Promise.resolve(res || selectedLocation);
      })
      .then((selectedLocation) => {
        var urlParams = {
          postal_code:
            selectedLocation.postalCode &&
            selectedLocation.postalCode.replace(/ .*/, ""),
          location: `${selectedLocation.lat},${selectedLocation.lng}`,
          address: selectedLocation.label,
          partner: partnerSummary.id,
          product: workoutType.id,
          start_time: _bookingTime,
        };

        if (selectedLocation.placeId) {
          delete urlParams.postal_code;

          urlParams.place = selectedLocation.value.place;
          urlParams.place_name = selectedLocation.value.name;
        }

        history.push(`/checkout?${toQueryString(urlParams)}`);
      });
  }, [
    selectedLocation,
    workoutType,
    _bookingTime,
    partnerSummary,
    availablePartners,
    history,
    handleCheckTimeSlotsClick,
    handleTimeSlotsClick,
  ]);

  const handleBookWithMeRequest = useCallback(
    (e) => {
      if (user) {
        sendBooking();
      } else {
        setBookingFlowContinue("bookingFlow");

        openSignUpModal();
      }
    },
    [user, sendBooking]
  );

  const continueBookingFlow = useCallback(() => {
    const query = new URLSearchParams(window.location.search);

    const bookingFlow = query.get(CONTINUE_BOOKING_FLOW_PARAM_NAME);

    if (!partnerSummary) {
      return;
    }

    if (bookingFlow) {
      if (bookingFlow === "bookingFlow") {
        sendBooking();
      } else {
        // TODO make this check a function
        if (clientWidth() < theme.responsive.breakpoints.lg) {
          openCheckTimeSlotsPanel();
        } else {
          openTimeSlotsPopup();
        }
      }

      query.delete(CONTINUE_BOOKING_FLOW_PARAM_NAME);
      history.replace({
        search: query.toString(),
      });
    }
  }, [user, sendBooking, openCheckTimeSlotsPanel, openTimeSlotsPopup]);

  // For Login by Phone Number
  useEffect(() => {
    continueBookingFlow();
  }, [continueBookingFlow]);

  const closeBookingFlowPanel = useCallback(() => {
    setIsBookingFlowPanelOpen(false);
    setIsBookingFlowPanelFullyOpen(false);
  }, []);

  const handleBottomReached = useCallback(() => {
    if (isBookingFlowPanelOpen) {
      setIsBookingFlowPanelOpen(false);
    }
    if (isReviewsModalOpen) {
      setIsReviewsModalOpen(false);
    }
  }, [isBookingFlowPanelOpen, isReviewsModalOpen]);

  const handleFullyOpen = useCallback(() => {
    setIsBookingFlowPanelFullyOpen(true);
  }, []);

  const handleWorkoutChange = useCallback(
    (workout) => {
      if (workout && (!workoutType || workoutType.id !== workout.id)) {
        setWorkoutType(workout);
      }
    },
    [workoutType]
  );

  const handleBookingTimeChange = useCallback((dateTime, isTimeSelected) => {
    // Analytics.trackEvent(TRAINERS_LIST_PAGE_CATEGORY, '', EVENT_DATETIME_CHANGE);

    setBookingTime(dateTime ? dateTime.getTime() : undefined);
  }, []);

  const closeBookingFlowPopup = useCallback(() => {
    setIsBookingFlowPopupOpen(false);
  }, []);

  const handleBookingFlowPopupCtaClick = useCallback(() => {
    if (bookingStep === "CHOOSE_DAY_TIME") {
      setBookingStep("CHOOSE_WORKOUT_TYPE");
    } else {
      handleBookWithMeRequest();
    }
  }, [bookingStep, handleBookWithMeRequest]);

  const handleSlideSwipe = useCallback(
    (oldIndex, newIndex) => {
      if (autoPlay) {
        setAutoPlay(false);
      }
    },
    [autoPlay]
  );

  var gymContent = null;

  if (partnerGyms && partnerGyms.length) {
    let trainingAt = partnerGyms.find(
      (v) => selectedLocation && v.name === selectedLocation.value.name
    );

    gymContent = (
      <div className="training-at tbk-mt-4 tbk-flex tbk-flex-wrap tbk-justify-center tbk-text-center">
        <div className="tbk-relative tbk--bottom-3 tbk-inline-flex tbk-min-w-0 tbk-items-center tbk-self-center tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-bg-basic-white tbk-px-1.5 tbk-py-0.5 tbk-text-blue-dark">
          <WeightIcon className="tbk-mr-0.5 tbk-shrink-0" />

          <div className="bundle-pricing-box-title tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-blue-dark">
            Training at
          </div>
        </div>
        <div className="tbk-flex tbk-min-w-full tbk-grow tbk-items-center tbk-justify-center tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-pb-4 tbk-pt-5 md:tbk-pl-7 md:tbk-pr-7">
          <div className="tbk-ml-3 tbk-mr-3 tbk-min-w-0">
            <div className="tbk-mt-1 tbk-flex tbk-items-center tbk-rounded-lg tbk-bg-grey-main tbk-p-1 tbk-text-basic-white">
              <WeightIcon className="tbk-mr-1" />
              <div className="tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap">
                {trainingAt
                  ? `${trainingAt.name} (${trainingAt.address}`
                  : `${partnerGyms[0].name} (${partnerGyms[0].address}`}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // const mediaBlockContent = [];
  //
  // if (partnerMedia && partnerMedia.length) {
  //   for (let i = 0; i < 8; i++) {
  //     if (partnerMedia[i]) {
  //       mediaBlockContent.push(
  //         <div
  //           role="button"
  //           className={`media-block-item media-block-item-${partnerMedia[
  //             i
  //           ].type.toLowerCase()} tbk-relative tbk-w-full tbk-overflow-hidden tbk-rounded-lg tbk-bg-grey-light tbk-pt-[95%]`}
  //           onClick={() => openGalleryCarouselModal(i)}
  //         >
  //           <img
  //             className="tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full"
  //             src={partnerMedia[i].thumbnail}
  //             style={{ width: "100%", height: "100%", objectFit: "cover" }}
  //             alt="Media block"
  //           />
  //         </div>
  //       );
  //     } else {
  //       mediaBlockContent.push(
  //         <div className="media-block-item empty tbk-relative tbk-w-full tbk-overflow-hidden tbk-rounded-lg tbk-bg-blue-grey-light tbk-pt-[95%] lg:tbk-bg-grey-light" />
  //       );
  //     }
  //   }
  // }

  const returnUrl = `${location.pathname}${
    location.search
      ? `${location.search}${
          bookingFlowContinue !== null
            ? `&${CONTINUE_BOOKING_FLOW_PARAM_NAME}=${bookingFlowContinue}`
            : ""
        }`
      : bookingFlowContinue !== null
      ? `?${CONTINUE_BOOKING_FLOW_PARAM_NAME}=${bookingFlowContinue}`
      : ""
  }`;

  const getAuthState = (authVendor, isLogin) => {
    switch (authVendor) {
      case "apple":
      case "google":
      case "facebook":
        return "setPhoneNumber";
      case "email":
        return "setEmail";
      default:
        return isLogin ? "codeVerificationLogin" : "codeVerification";
    }
  };

  /**
   * Handles the logic for building the login parameters.
   * @param authVendor
   * @param params
   * @param userPhoneCode
   * @param userPhoneNumber
   * @returns {string} - string with the correct query parameters.
   */
  const getLoginParams = (
    authVendor,
    params,
    userPhoneCode,
    userPhoneNumber
  ) => {
    let queryStr = "";

    if (authVendor) {
      queryStr += `&authVendor=${authVendor}`;
    }

    if (userPhoneCode) {
      queryStr += `&phoneCode=${userPhoneCode}`;
    }

    if (userPhoneNumber) {
      queryStr += `&phoneNumber=${userPhoneNumber}`;
    }

    if (authVendor === "facebook") {
      queryStr += `&accessToken=${params.token}`;
    }

    if (authVendor === "google") {
      queryStr += `&identityToken=${params.token}`;
    }

    if (authVendor === "google" || authVendor === "facebook") {
      queryStr += `&firstName=${params.firstName}`;
      queryStr += `&lastName=${params.lastName}`;
      queryStr += `&email=${params.email}`;
    }

    return queryStr;
  };

  /**
   * This function is responsible for setting the authState, setting the referral cookie,
   * and redirecting to the login page with the correct parameters.
   */
  const handleNext = (authVendor, params, isLogin, isEmailLogin) => {
    const nextAuthState = getAuthState(authVendor, isLogin);
    setAuthState(nextAuthState);

    // Cookies.set("referral", process.env.REACT_APP_SIGN_UP_REFERRAL_CODE, {
    //   path: "/",
    // });

    let search = `?authState=${nextAuthState}`;
    search += getLoginParams(
      authVendor,
      params,
      userPhoneCode,
      userPhoneNumber
    );

    history.push({
      pathname: "/login",
      search,
      state: {
        from:
          bookingFlowContinue !== null
            ? makeContinueBookingFlowUrl(
                location,
                CONTINUE_BOOKING_FLOW_PARAM_NAME,
                bookingFlowContinue
              )
            : "",
      },
    });
  };

  const metaDescription =
    partnerSummary && partnerSummary.metaDescription
      ? partnerSummary.metaDescription
      : `On-demand, in person, personal training in ${
          region === "US" ? "Los Angeles" : "London"
        }.`;

  return (
    <>
      <ScrollToTopOnMount />
      {/*<Title>Personal Training in London | TruBe</Title>*/}
      <Meta name="description" content={metaDescription} />
      <Header
        showLinks
        isFixed
        onViewCoachesNavLinkClick={() => clearAvailablePartnersLoaded()}
      />

      <div />

      <div className="tbk-relative tbk-overflow-hidden tbk-bg-basic-trueblack tbk-pt-96 lg:tbk-pt-0">
        <div className="tbk-fixed tbk-left-0 tbk-right-0 tbk-top-7 lg:tbk-static lg:tbk-bg-grey-light">
          <div className="hero-container tbk-container tbk-relative tbk-mx-auto md:tbk-px-2">
            <div className="hero-content tbk-flex tbk-h-96 lg:tbk-grid lg:tbk-h-auto lg:tbk-grid-cols-hero lg:tbk-items-start lg:tbk-gap-4 lg:tbk-pt-3">
              <div className="hero-col tbk-w-full tbk-max-w-full tbk-shrink-0 lg:tbk-mr-0 lg:tbk-px-0 lg:tbk-pb-2 lg:tbk-pt-0">
                <div
                  className={`profile-picture media-slider ${
                    !partnerMedia || partnerMedia.length <= 1 ? "no-media" : ""
                  } ${
                    !trainerPictureUrl ? "tbk-justify-center" : ""
                  } tbk-relative tbk-flex tbk-h-full tbk-w-full tbk-flex-col tbk-overflow-hidden lg:tbk-m-0 lg:tbk-h-108 lg:tbk-w-108 lg:tbk-rounded-2xl`}
                >
                  {slickInitialised &&
                  partnerMedia &&
                  partnerMedia.length > 1 ? (
                    <Slider
                      className="coach-media-carousel tbk--ml-2.5_ tbk--mr-2.5_"
                      dots={true}
                      dotsClass={"slick-dots tbk-pl-3"}
                      appendDots={(dots) => (
                        <ul onClick={handleSlideSwipe}>
                          {dots.map((item, index) => {
                            return (
                              <li key={index} className={item.props.className}>
                                {item.props.children}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      arrows={clientWidth() > theme.responsive.breakpoints.lg}
                      autoplay={autoPlay}
                      autoplaySpeed={4000}
                      pauseOnFocus
                      pauseOnDotsHover
                      pauseOnHover
                      infinite={true}
                      speed={500}
                      slidesToShow={1}
                      slidesToScroll={1}
                      initialSlide={0}
                      responsive={reviewsSliderResponsiveSettings}
                      nextArrow={
                        <SliderNextButton onBtnClick={handleSlideSwipe} />
                      }
                      prevArrow={
                        <SliderPrevButton onBtnClick={handleSlideSwipe} />
                      }
                      onSwipe={handleSlideSwipe}
                    >
                      {partnerMedia
                        .filter((val) => val.type === "IMAGE")
                        .map((val, index) => {
                          return (
                            <div
                              id={`slide-${index}`}
                              className="slide partner-review-item _tbk-pl-2.5 _tbk-pr-2.5"
                              key={index}
                              onClick={() =>
                                openGalleryCarouselModal(val.index)
                              }
                            >
                              <div className="content slide-bg tbk-h-full tbk-w-full">
                                <div
                                  className="tb-image-container tbk-relative tbk-h-full tbk-overflow-hidden tbk-pt-100 lg:tbk-pt-full"
                                  style={
                                    {
                                      // paddingTop: "100%",
                                    }
                                  }
                                >
                                  <img
                                    className="tbk-absolute tbk-top-0 tbk-h-full tbk-w-full tbk-object-cover lg:tbk-rounded-2xl"
                                    itemProp="image"
                                    src={val.url}
                                    alt={`${partnerFirstName} ${partnerFamilyName} | Personal Trainer in Los Angeles`}
                                  />
                                </div>
                              </div>
                              {/*{index !== partnerReviews.length - 1 ? <hr /> : null}*/}
                            </div>
                          );
                        })}
                    </Slider>
                  ) : trainerPictureUrl ? (
                    <div
                      className="tb-image-container tbk-relative tbk-overflow-hidden tbk-pt-100 lg:tbk-pt-full"
                      style={
                        {
                          // paddingTop: "100%",
                        }
                      }
                    >
                      <img
                        className="tbk-absolute tbk-top-0 tbk-h-full tbk-w-full tbk-object-cover lg:tbk-rounded-2xl"
                        itemProp="image"
                        src={
                          trainerPictureUrl
                            ? trainerPictureUrl
                            : defaultPartnerPhoto
                        }
                        alt={`${partnerFirstName} ${partnerFamilyName} | Personal Trainer in Los Angeles`}
                      />
                    </div>
                  ) : (
                    <div className="tbk-py-5 sm:tbk-mt-[10px]">
                      <Lottie
                        options={defaultOptions}
                        height={
                          clientWidth() < theme.responsive.breakpoints.sm
                            ? 116
                            : 112
                        }
                        width={
                          clientWidth() < theme.responsive.breakpoints.sm
                            ? 116
                            : 112
                        }
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="tbk-mb-2 tbk-min-w-0 tbk-self-start">
                <div className="tbk-hidden tbk-items-center tbk-justify-between lg:tbk-flex">
                  <div className="tbk-text-small md:tbk-text-main tbk-text-blue-grey">
                    {partnerLoaded && partnerSummary.city
                      ? partnerSummary.city
                      : "Los Angeles"}
                    ,{" "}
                    {partnerLoaded && partnerSummary.region
                      ? partnerSummary.region
                      : "USA"}
                  </div>
                  <button
                    className="tbk-text-small tbk-flex tbk-items-center tbk-text-grey"
                    onClick={openShareModal}
                  >
                    <ShareIcon className="tbk-mr-1" size={16} />
                    Share profile
                  </button>
                </div>
                <h1 className="tbk-text-title-bold lg:tbk-text-epic-bold tbk-mb-2 tbk-mt-3 tbk-hidden tbk-text-primary lg:tbk-mt-1 lg:tbk-block">
                  {partnerFirstName}&nbsp;
                  <br className="lg:tbk-hidden" />
                  {partnerFamilyName}
                </h1>
                <ul className="tb-disciplines-skills-list tbk-mb-3 tbk-hidden lg:tbk-mb-2 lg:tbk-flex lg:tbk-flex-wrap">
                  {partnerProducts &&
                    partnerProducts.map((val) => (
                      <PartnerProductBadge
                        key={val.id}
                        className="tbk-mb-1 tbk-mr-1 tbk-px-2 tbk-py-1"
                        iconClassName="tbk-mr-0.5"
                        labelClassName="tbk-text-main"
                        product={val}
                      />
                    ))}
                  <li className="tb-disciplines-skills-br" />
                </ul>
                <div className="tbk-mb-2 tbk-flex tbk-hidden tbk-justify-center lg:tbk-block lg:tbk-justify-start">
                  <div className="tbk-mr-7">
                    <ul className="tbk-mb-3 tbk-flex tbk-flex-wrap tbk-justify-center lg:tbk-justify-start">
                      {partnerLoaded &&
                      partnerSummary.rewards &&
                      partnerSummary.rewards.length
                        ? partnerSummary.rewards.map((reward, index) => (
                            <li
                              key={index}
                              className="tbk-mr-5 tbk-flex tbk-items-center"
                            >
                              <RewardBadge label={reward} size={24} />
                            </li>
                          ))
                        : null}
                    </ul>
                  </div>
                </div>
                <div className="tbk-mb-4 tbk-hidden tbk-items-center md:tbk-mb-0 lg:tbk-flex">
                  <div
                    className=""
                    itemProp="itemReviewed"
                    itemScope
                    itemType="https://schema.org/Product"
                  >
                    <meta
                      itemProp="name"
                      content={`${partnerFirstName} ${partnerFamilyName}`}
                    />
                    <meta
                      itemProp="image"
                      content={
                        trainerPictureUrl ? trainerPictureUrl : undefined
                      }
                    />
                    {partnerLoaded && partnerReviewsNumber ? (
                      <TrainerRating
                        className="tbk-text-main tbk-mb-1 tbk-flex md:tbk-mb-0 md:tbk-justify-start lg:tbk-mr-5"
                        averageRatingClassName="tbk-text-h3-subtitle tbk-text-primary"
                        numberOfSessionsClassName="tb-link-like tbk-text-small md:tbk-text-main tbk-text-blue-grey tbk-underline"
                        partner={{
                          ...partnerSummary,
                          partnerRatingRecap: {
                            average: partnerSummary.rating,
                            numberOfRatings: partnerReviewsNumber,
                          },
                        }}
                        onClick={openReviewsModal}
                      />
                    ) : null}
                  </div>
                </div>

                {showReviewsSlider &&
                clientWidth() >= theme.responsive.breakpoints.xl ? (
                  <>
                    {/*<h2 className="tbk-text-cta md:tbk-text-title-bold_ tbk-text-uppercase tbk-mb-2_ tbk-text-primary">*/}
                    {/*  Reviews*/}
                    {/*</h2>*/}
                    <div
                      className="coach-reviews tbk-grid tbk-grid-cols-coach-profile-reviews"
                      // className={`${
                      //   mediaBlockContent && mediaBlockContent.length
                      //     ? "tbk-mb-6 lg:tbk-mb-8"
                      //     : "tbk-mb-26 lg:tbk-mb-8"
                      // }`}
                    >
                      {slickInitialised && (
                        <Slider
                          className="trainer-profile-reviews-carousel tbk--ml-2.5 tbk--mr-2.5"
                          dots={numberOfReviewsToShow > 2}
                          dotsClass={"slick-dots tbk-pl-3"}
                          arrows={false}
                          swipe={false}
                          autoplay
                          autoplaySpeed={5000}
                          infinite={true}
                          speed={500}
                          slidesToShow={1}
                          slidesToScroll={1}
                          responsive={reviewsSliderResponsiveSettings}
                        >
                          {partnerReviews
                            .filter((v) => v.review)
                            .slice(0, 4)
                            .map((val, index) => {
                              var stars = [];

                              for (var i = 0; i < val.rating; i++) {
                                stars.push(
                                  <StarFullIcon
                                    className="tbk-inline-block tbk-text-coral"
                                    key={i}
                                  />
                                );
                              }

                              return (
                                <div
                                  id={`slide-${index}`}
                                  className="slide partner-review-item tbk-cursor-pointer tbk-pl-2.5 tbk-pr-2.5 tbk-pt-2"
                                  key={index}
                                  onClick={() => openReviewsModal()}
                                >
                                  <div className="content slide-bg tbk-h-full tbk-w-full">
                                    <div className="partner-review-item-content tbk-relative tbk-pb-1.5 after:tbk-absolute after:tbk-bottom-0 after:tbk-left-7 after:tbk-h-1.5 after:tbk-w-[28px] after:tbk-bg-review-arrow after:tbk-content-['']">
                                      <div className="tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube">
                                        <div className="tbk-mb-1">{stars}</div>
                                        <ClampLines
                                          text={val.review || ""}
                                          id={"coach-review-comment"}
                                          className="tbk-text-main tbk-text-primary"
                                          lines={2}
                                          ellipsis="..."
                                          buttons={false}
                                        />
                                      </div>
                                    </div>
                                    <div className="tbk-text-secondary tbk-mt-1 tbk-text-primary">
                                      <p className="tbk-mb-1">
                                        {val.customerFirstName}
                                        {val.customerLastName
                                          ? ` ${val.customerLastName.charAt(
                                              0
                                            )}.`
                                          : ""}{" "}
                                        <span className="tbk-text-grey-inactive">
                                          after
                                        </span>{" "}
                                        {val.productName} Session{" "}
                                        <span className="tbk-text-grey-inactive">
                                          (
                                          {getMomentWithTz(
                                            moment.utc(val.date),
                                            region
                                          ).format("DD MMM YYYY")}
                                          )
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                  {/*{index !== partnerReviews.length - 1 ? <hr /> : null}*/}
                                </div>
                              );
                            })}
                        </Slider>
                      )}
                      {/*{false && numberOfReviewsToShow > 2 && (*/}
                      {/*  <button*/}
                      {/*    className={`tb-session-link virtual tb-mg-t-0 tbk-text-main ${*/}
                      {/*      partnerReviews && partnerReviewsNumber*/}
                      {/*        ? "tbk-text-basic-violet"*/}
                      {/*        : "tbk-text-grey-inactive"*/}
                      {/*    } tbk-float-right hover:tbk-text-blue-grey-light`}*/}
                      {/*    iconClass="tbk-c-basic-violet"*/}
                      {/*    onClick={() => openReviewsModal()}*/}
                      {/*    disabled={*/}
                      {/*      !partnerReviews || numberOfReviewsToShow === 0*/}
                      {/*    }*/}
                      {/*  >*/}
                      {/*    See all reviews*/}
                      {/*    <ArrowRightIcon className="tbk-inline-block" />*/}
                      {/*  </button>*/}
                      {/*)}*/}
                    </div>
                  </>
                ) : null}

                {/*<button*/}
                {/*  className="tbk-text-small tbk-mb-3 tbk-hidden tbk-items-center tbk-rounded-lg tbk-bg-basic-white tbk-py-1 tbk-px-2 tbk-text-primary lg:tbk-mb-5 lg:tbk-flex"*/}
                {/*  onClick={openShareModal}*/}
                {/*>*/}
                {/*  <div className="tbk-mr-1 tbk-inline-block">*/}
                {/*    <ShareIcon size={16} />*/}
                {/*  </div>*/}
                {/*  Share profile*/}
                {/*</button>*/}
              </div>
            </div>
          </div>
        </div>

        <div className="tbk--mt-3_ tbk-relative tbk-z-1 tbk-overflow-hidden tbk-rounded-t-lg tbk-bg-basic-white tbk-pb-3 tbk-pt-2 lg:tbk-hidden lg:tbk-pt-5">
          <div className="tbk-container tbk-relative tbk-mx-auto tbk-px-2">
            <div className="lg:tbk-mb-0">
              <h1 className="tbk-text-h2-black tbk-mb-1 !tbk-leading-tight tbk-text-primary lg:tbk-hidden">
                {partnerFirstName}&nbsp;{partnerFamilyName}
              </h1>
              <div className="tbk-mb-3 tbk-flex tbk-items-center tbk-justify-between lg:tbk-hidden">
                <div className="">
                  <div
                    className=""
                    itemProp="itemReviewed"
                    itemScope
                    itemType="https://schema.org/Product"
                  >
                    <meta
                      itemProp="name"
                      content={`${partnerFirstName} ${partnerFamilyName}`}
                    />
                    <meta
                      itemProp="image"
                      content={
                        trainerPictureUrl ? trainerPictureUrl : undefined
                      }
                    />
                    {partnerLoaded && partnerReviewsNumber ? (
                      <TrainerRating
                        className="tbk-text-main tbk-flex md:tbk-mb-0 md:tbk-justify-start"
                        averageRatingClassName="tbk-text-h3-subtitle tbk-text-primary"
                        numberOfSessionsClassName="tb-link-like tbk-text-small md:tbk-text-main tbk-text-blue-grey tbk-underline"
                        partner={{
                          ...partnerSummary,
                          partnerRatingRecap: {
                            average: partnerSummary.rating,
                            numberOfRatings: partnerReviewsNumber,
                          },
                        }}
                        onClick={openReviewsModal}
                      />
                    ) : null}
                  </div>
                </div>
                <div className="tbk-text-small md:tbk-text-main tbk-text-blue-grey">
                  {partnerLoaded && partnerSummary.city
                    ? partnerSummary.city
                    : "Los Angeles"}
                  ,{" "}
                  {partnerLoaded && partnerSummary.region
                    ? partnerSummary.region
                    : "USA"}
                </div>
              </div>
              <ul className="tb-disciplines-skills-list tbk-mb-2 tbk-flex tbk-flex-wrap lg:tbk-hidden">
                {partnerProducts &&
                  partnerProducts.map((val) => (
                    <PartnerProductBadge
                      key={val.id}
                      className="tbk-mb-1 tbk-mr-1 tbk-px-2 tbk-py-1"
                      iconClassName="tbk-mr-0.5"
                      labelClassName="tbk-text-main"
                      product={val}
                    />
                  ))}
                <li className="tb-disciplines-skills-br" />
              </ul>
              <div className="tbk-mb-2 lg:tbk-mb-5 lg:tbk-hidden">
                <ul className="tbk-mb-0 tbk-flex tbk-flex-wrap md:tbk-justify-start">
                  {partnerLoaded &&
                  partnerSummary.rewards &&
                  partnerSummary.rewards.length
                    ? partnerSummary.rewards.map((reward, index) => (
                        <li
                          key={index}
                          className={`tbk-mb-2${
                            partnerSummary.rewards.length - 1 !== index
                              ? " tbk-mr-3"
                              : ""
                          }`}
                        >
                          <RewardBadge key={index} label={reward} size={24} />
                        </li>
                      ))
                    : null}
                </ul>
              </div>

              <Button
                className={`tbk-mb-4 tbk-mt-4 tbk-w-full`}
                color="green"
                startIcon={<CalendarIcon size={32} />}
                endIcon={<CaretRightIcon />}
                onClick={handleCheckTimeSlotsClick}
              >
                Availability
              </Button>

              {showReviewsSlider &&
              clientWidth() < theme.responsive.breakpoints.xl ? (
                <>
                  {/*<h2 className="tbk-text-cta md:tbk-text-title-bold_ tbk-text-uppercase tbk-mb-2_ tbk-text-primary">*/}
                  {/*  Reviews*/}
                  {/*</h2>*/}
                  <div
                    className="tbk-mb-7"
                    // className={`${
                    //   mediaBlockContent && mediaBlockContent.length
                    //     ? "tbk-mb-6 lg:tbk-mb-8"
                    //     : "tbk-mb-26 lg:tbk-mb-8"
                    // }`}
                  >
                    {slickInitialised && (
                      <Slider
                        className="trainer-profile-reviews-carousel tbk--ml-2.5 tbk--mr-2.5"
                        dots={numberOfReviewsToShow > 2}
                        dotsClass={"slick-dots tbk-pl-3"}
                        arrows={false}
                        autoplay
                        autoplaySpeed={5000}
                        infinite={true}
                        speed={500}
                        slidesToShow={1}
                        slidesToScroll={1}
                        responsive={reviewsSliderResponsiveSettings}
                      >
                        {partnerReviews
                          .filter((v) => v.review)
                          .slice(0, 4)
                          .map((val, index) => {
                            var stars = [];

                            for (var i = 0; i < val.rating; i++) {
                              stars.push(
                                <StarFullIcon
                                  className="tbk-inline-block tbk-text-coral"
                                  key={i}
                                />
                              );
                            }

                            return (
                              <div
                                id={`slide-${index}`}
                                className="slide partner-review-item tbk-pl-2.5 tbk-pr-2.5 tbk-pt-2"
                                key={index}
                                onClick={() => openReviewsModal()}
                              >
                                <div className="content slide-bg tbk-h-full tbk-w-full">
                                  <div className="partner-review-item-content tbk-relative tbk-pb-1.5 after:tbk-absolute after:tbk-bottom-0 after:tbk-left-7 after:tbk-h-1.5 after:tbk-w-[28px] after:tbk-bg-review-arrow after:tbk-content-['']">
                                    <div className="tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube">
                                      <div className="tbk-mb-1">{stars}</div>
                                      <ClampLines
                                        text={val.review || ""}
                                        id={"coach-review-comment"}
                                        className="tbk-text-main tbk-text-primary"
                                        lines={3}
                                        ellipsis="..."
                                        buttons={false}
                                      />
                                    </div>
                                  </div>
                                  <div className="tbk-text-secondary tbk-mt-1 tbk-text-primary">
                                    <p className="tbk-mb-1">
                                      {val.customerFirstName}
                                      {val.customerLastName
                                        ? ` ${val.customerLastName.charAt(0)}.`
                                        : ""}{" "}
                                      <span className="tbk-text-grey-inactive">
                                        after
                                      </span>{" "}
                                      {val.productName} Session{" "}
                                      <span className="tbk-text-grey-inactive">
                                        (
                                        {getMomentWithTz(
                                          moment.utc(val.date),
                                          region
                                        ).format("DD MMM YYYY")}
                                        )
                                      </span>
                                    </p>
                                  </div>
                                </div>
                                {/*{index !== partnerReviews.length - 1 ? <hr /> : null}*/}
                              </div>
                            );
                          })}
                      </Slider>
                    )}
                    {false && numberOfReviewsToShow > 2 && (
                      <button
                        className={`tb-session-link virtual tb-mg-t-0 tbk-text-main ${
                          partnerReviews && partnerReviewsNumber
                            ? "tbk-text-basic-violet"
                            : "tbk-text-grey-inactive"
                        } tbk-float-right hover:tbk-text-blue-grey-light`}
                        iconClass="tbk-c-basic-violet"
                        onClick={() => openReviewsModal()}
                        disabled={
                          !partnerReviews || numberOfReviewsToShow === 0
                        }
                      >
                        See all reviews
                        <ArrowRightIcon className="tbk-inline-block" />
                      </button>
                    )}
                  </div>
                </>
              ) : null}

              <div className="tbk-mb-5 tbk-text-center md:tbk-hidden">
                <div className="tbk-text-small tbk-text-blue-grey">
                  Have any questions?
                </div>
                <div className="tbk-relative tbk-inline-flex tbk-justify-center">
                  <div className="tbk-mr-1">
                    <ContactIcon />
                  </div>

                  {isMobile ? (
                    <a
                      href={"sms:+13233872101"}
                      target="_blank"
                      rel="noreferrer"
                      className="tbk-text-main tbk-text-primary tbk-underline"
                    >
                      Speak to a Specialist
                    </a>
                  ) : (
                    <Link
                      to="/contact"
                      target="_blank"
                      className="tbk-text-main tbk-text-primary tbk-underline"
                    >
                      Speak to a Specialist
                    </Link>
                  )}
                </div>
              </div>

              <BundlePriceBox
                bundles={bundles}
                customerBundles={[]}
                partnerId={partnerSummary && partnerSummary.id}
                partnerFirstName={partnerFirstName}
                price={
                  partnerPrice && partnerPrice.length && partnerPrice[0].price
                }
              />

              {gymContent}
            </div>
          </div>
        </div>

        <div className="tbk-relative tbk-bg-grey-light tbk-pt-4 lg:tbk-bg-basic-white lg:tbk-pt-5">
          <div className="tbk-container tbk-relative tbk-mx-auto xl:tbk-px-2">
            <div className="tbk-px-2 lg:tbk-grid lg:tbk-grid-cols-trainer-profile-layout lg:tbk-gap-34 xl:tbk-px-0">
              <div className="">
                <h2 className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-2 tbk-text-primary">
                  Bio
                </h2>
                <div className="tbk-mb-7 lg:tbk-mb-0">
                  <ReadMore
                    text={partnerLoaded ? partnerSummary.biography : ""}
                    position="right"
                  />

                  <div className="trainer-experience tbk-relative tbk-mb-4 tbk-mt-8">
                    <i className="tbk-absolute tbk-top-[6px]">
                      <ExperienceIcon />
                    </i>

                    <h2 className="tbk-text-cta tbk-mb-0.5 tbk-ml-5 tbk-pl-1 tbk-uppercase tbk-text-primary">
                      Experience:
                    </h2>
                    <p className="tbk-text-main-large tbk-ml-5 tbk-pl-1 tbk-text-primary">
                      {Math.max(1, experience)}+ years
                    </p>
                  </div>

                  <div className="trainer-qualifications tbk-relative">
                    <i className="tbk-absolute tbk-top-[6px]">
                      <QualificationIcon />
                    </i>

                    <h2 className="tbk-text-cta tbk-mb-0.5 tbk-ml-5 tbk-mt-4 tbk-pl-1 tbk-uppercase tbk-text-primary">
                      Qualification:
                    </h2>
                    <ul className="tbk-mb-0 md:tbk-mb-3">
                      {partnerLoaded &&
                        partnerSummary.qualifications.map(
                          (qualification, index) => (
                            <li
                              key={index}
                              className="tbk-text-main-large tbk-ml-5 tbk-pl-1 tbk-text-primary"
                            >
                              {qualification}
                            </li>
                          )
                        )}

                      {partnerLoaded &&
                      partnerSummary.qualifications.length === 0 ? (
                        <li className="tbk-text-main-large tbk-ml-5 tbk-pl-1 tbk-text-primary">
                          None
                        </li>
                      ) : null}
                    </ul>
                  </div>

                  {partnerLoaded &&
                  partnerSummary.languages &&
                  partnerSummary.languages.length ? (
                    <div className="trainer-languages tbk-relative">
                      <i className="tbk-absolute tbk-top-[6px]">
                        <LanguagesIcon />
                      </i>
                      <h2 className="tbk-text-cta tbk-mb-0.5 tbk-ml-5 tbk-mt-4 tbk-pl-1 tbk-uppercase tbk-text-primary">
                        Languages:
                      </h2>

                      {partnerSummary.languages.map((lng, index, arr) => {
                        let emoji;

                        if (isAndroidDevice || isMac || isIosDevice) {
                          emoji = lng.region
                            .toUpperCase()
                            .replace(/./g, (char) =>
                              String.fromCodePoint(char.charCodeAt(0) + OFFSET)
                            );
                        } else {
                          let countryInfo = getCountryInfo(lng.region);

                          emoji = countryInfo[2];
                        }

                        return (
                          <span
                            key={index}
                            className={`${
                              index === 0 ? "tbk-ml-5 " : ""
                            }tbk-text-main-large tbk-pl-1 tbk-text-primary`}
                          >
                            {isAndroidDevice || isMac || isIosDevice ? (
                              emoji
                            ) : (
                              <img
                                src={emoji}
                                className="tbk-inline-block tbk-w-3"
                              />
                            )}{" "}
                            {lng.name}
                            {index < arr.length - 1 ? ", " : ""}
                          </span>
                        );
                      })}
                    </div>
                  ) : null}

                  {partnerLoaded && partnerSummary.proudestMoment ? (
                    <div className="tbk-mt-5">
                      <div className="trainer-facts tbk-relative tbk-mb-3">
                        <i className="tbk-absolute tbk-top-[6px]">
                          <MiscIcon />
                        </i>
                        <div className="tbk-text-cta tbk-mb-0.5 tbk-ml-5 tbk-pl-1 tbk-uppercase tbk-text-primary">
                          Proudest moment as a{" "}
                          <FormattedMessage
                            id="trainer"
                            defaultMessage="trainer"
                          />
                          :
                        </div>
                        <div className="tbk-text-main-large tbk-ml-5 tbk-pl-1 tbk-text-primary">
                          {partnerSummary.proudestMoment}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {partnerLoaded &&
                  partnerSummary.specialisations &&
                  partnerSummary.specialisations.length ? (
                    <h2 className="tbk-text-cta tbk-mb-2 tbk-mt-7 tbk-text-primary lg:tbk-mt-8">
                      <FormattedMessage
                        id="specialisations"
                        defaultMessage="Specializations"
                      />
                      :
                    </h2>
                  ) : null}
                  <ul className="tbk-grid tbk-grid-cols-4 tbk-gap-1 lg:tbk-gap-2 xl:tbk-grid-cols-6">
                    {partnerLoaded &&
                      partnerSummary.specialisations
                        .slice(
                          0,
                          showMoreSpecialisations
                            ? partnerSummary.specialisations.length
                            : clientWidth() >= theme.responsive.breakpoints.xl
                            ? 6
                            : 4
                        )
                        .map((specialisation, index) => (
                          <li
                            key={index}
                            className="tbk-text-main-large tbk-flex tbk-h-28 tbk-flex-col tbk-items-center tbk-rounded-2xl tbk-border tbk-border-blue-grey-light tbk-bg-basic-white tbk-px-2 tbk-pb-0.5 tbk-pt-2 tbk-text-primary"
                          >
                            <img
                              className="tbk-mb-0.5"
                              width="48"
                              height="48"
                              src={specialisation.photo}
                              alt={specialisation.name}
                            />
                            <div className="tbk-text-small tbk-text-center tbk-text-primary">
                              {specialisation.name}
                            </div>
                          </li>
                        ))}
                  </ul>
                  {partnerLoaded &&
                  partnerSummary.specialisations &&
                  partnerSummary.specialisations.length > 6 &&
                  !showMoreSpecialisations ? (
                    <div className="tbk-relative tbk-mb-1 tbk-mt-2 tbk-text-center">
                      <button
                        className="tbk-inline-block tbk-bg-grey-light tbk-px-[19px] tbk-py-0 lg:tbk-bg-basic-white"
                        onClick={() => setShowMoreSpecialisations(true)}
                      >
                        <div className="tbk-flex tbk-text-blue">
                          Show All Specializations
                          <CaretDownIcon />
                        </div>
                      </button>
                      <hr className="tbk-mx-auto tbk--mt-1.5 tbk-mb-0 tbk-border-blue-grey-light" />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="tbk-sticky tbk-hidden tbk-bg-basic-white md:tbk-block">
                <div className="tbk-max-w-full">
                  {clientWidth() >= theme.responsive.breakpoints.lg &&
                  slickInitialised ? (
                    <BookingBox
                      trainerFirstName={partnerFirstName}
                      onTimeSlotsClick={handleTimeSlotsClick}
                      onSubmit={handleBookWithMeRequest}
                    />
                  ) : null}
                  {clientWidth() >= theme.responsive.breakpoints.lg ? (
                    <div>
                      {slickInitialised ? (
                        <BundlePriceBox
                          bundles={bundles}
                          customerBundles={[]}
                          partnerId={partnerLoaded && partnerSummary.id}
                          partnerFirstName={partnerFirstName}
                          price={
                            partnerPrice &&
                            partnerPrice.length &&
                            partnerPrice[0].price
                          }
                        />
                      ) : null}
                      {gymContent}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="tbk-justify-between tbk-overflow-hidden tbk-px-2 tbk-pt-2 lg:tbk-grid lg:tbk-grid-cols-trainer-profile-layout lg:tbk-gap-34 lg:tbk-overflow-visible lg:tbk-pt-8 xl:tbk-px-0">
              <div className="tbk-min-w-0">
                {/*{false &&*/}
                {/*mediaBlockContent &&*/}
                {/*mediaBlockContent.length &&*/}
                {/*clientWidth() < theme.responsive.breakpoints.lg ? (*/}
                {/*  <div className="tbk-mb-7 lg:tbk-mb-8">*/}
                {/*    <>*/}
                {/*      <h2 className="tbk-text-h3-subtitle lg:tbk-text-cta tbk-mb-2 tbk-uppercase tbk-text-primary">*/}
                {/*        Media*/}
                {/*      </h2>*/}
                {/*      <div className="media-block tbk-grid tbk-grid-cols-4 tbk-gap-1 lg:tbk-gap-2">*/}
                {/*        {mediaBlockContent}*/}
                {/*      </div>*/}
                {/*    </>*/}
                {/*  </div>*/}
                {/*) : null}*/}

                {partnerLoaded && partnerSummary.commitment && (
                  <div className="tbk-mb-7 lg:tbk-mb-8">
                    <div className="with-quotes tbk-relative tbk-rounded-lg tbk-bg-basic-white tbk-p-3 tbk-shadow-trube md:tbk-p-7">
                      <div
                        className="tbk-absolute tbk-text-primary"
                        style={{
                          top: "-14px",
                          left: "14px",
                        }}
                      >
                        <svg
                          width="29"
                          height="24"
                          viewBox="0 0 29 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.206 11.4H7.544L10.763 0.299995H5.768L0.44 12.621V23.166H12.206V11.4ZM28.412 11.4H23.75L26.969 0.299995H21.974L16.646 12.621V23.166H28.412V11.4Z"
                            fill="#203B54"
                          />
                        </svg>
                      </div>
                      <div className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-3 tbk-text-primary">
                        My Commitment to You
                      </div>
                      <p className="tbk-text-main tbk-text-primary">
                        {partnerSummary.commitment}
                      </p>
                      <div
                        className="tbk-absolute tbk-text-primary"
                        style={{
                          bottom: "-10px",
                          right: "14px",
                        }}
                      >
                        <svg
                          width="29"
                          height="24"
                          viewBox="0 0 29 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16.9219 12.4574L21.5837 12.4986L18.2667 23.5697L23.2615 23.6138L28.6982 11.3404L28.7914 0.795794L17.0258 0.691835L16.9219 12.4574ZM0.716494 12.3142L5.37831 12.3554L2.06136 23.4265L7.05617 23.4706L12.4928 11.1972L12.586 0.652605L0.820453 0.548647L0.716494 12.3142Z"
                            fill="#203B54"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="lg:tbk-mb-8">
                {/*{false &&*/}
                {/*mediaBlockContent &&*/}
                {/*mediaBlockContent.length &&*/}
                {/*clientWidth() >= theme.responsive.breakpoints.lg ? (*/}
                {/*  <div className="tbk-mb-7 lg:tbk-mb-8">*/}
                {/*    <>*/}
                {/*      <h2 className="tbk-text-h3-subtitle lg:tbk-text-cta tbk-mb-2 tbk-uppercase tbk-text-primary">*/}
                {/*        Media*/}
                {/*      </h2>*/}
                {/*      <div className="media-block tbk-grid tbk-grid-cols-4 tbk-gap-2">*/}
                {/*        {mediaBlockContent}*/}
                {/*      </div>*/}
                {/*    </>*/}
                {/*  </div>*/}
                {/*) : null}*/}
                {mantra && (
                  <div className="tbk-mb-5 tbk-mt-7 lg:tbk-mt-0">
                    <div className="tbk-text-h3-subtitle lg:tbk-text-cta tbk-mb-2 tbk-text-center tbk-text-primary">
                      Mantra:
                    </div>
                    <div className="tbk-text-h2-black tbk-text-center tbk-text-primary">
                      {mantra.split("\n").map((line, index) => (
                        <div key={index}>{line}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <hr className="tbk-mx-2 tbk-mb-0 xl:tbk-mx-0" />
          </div>
        </div>

        <section className="everything-you-need-section tbk-relative tbk--mt-1 tbk-bg-grey-light tbk-pb-3 tbk-pt-7 md:tbk-pb-7 lg:tbk-mt-0 lg:tbk-bg-basic-white">
          <div className="tbk-container tbk-mx-auto tbk-px-2">
            <h2 className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-2 tbk-text-primary">
              Everything you need
            </h2>
            <div className="tbk-items-stretch tbk-justify-between lg:tbk-grid lg:tbk-grid-cols-4 lg:tbk-gap-3">
              <div className="tbk-mb-2 lg:tbk-mb-7">
                <div className="tbk-h-full tbk-rounded-lg tbk-bg-basic-white tbk-p-2 tbk-text-left md:tbk-p-3 lg:tbk-bg-grey-light">
                  <div className="tbk-mb-2 tbk-flex tbk-items-center lg:tbk-mb-0 lg:tbk-block">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="tbk-mr-2 tbk-shrink-0"
                    >
                      <path
                        d="M16 7C17.9313 7 19.5 5.43125 19.5 3.5C19.5 1.56875 17.9313 0 16 0C14.0688 0 12.5 1.56875 12.5 3.5C12.5 5.43125 14.0688 7 16 7Z"
                        fill="#3AA1FF"
                      />
                      <path
                        d="M27.0004 7.05L26.9691 7.05625L26.9441 7.0625C26.8816 7.08125 26.8191 7.1 26.7566 7.11875C25.5941 7.4625 19.9504 9.05 15.9691 9.05C12.2754 9.05 7.13789 7.675 5.49414 7.20625C5.33164 7.14375 5.16289 7.0875 4.99414 7.04375C3.80664 6.73125 2.99414 7.9375 2.99414 9.0375C2.99414 10.1313 3.97539 10.65 4.96289 11.025V11.0438L10.9129 12.9C11.5191 13.1313 11.6816 13.3688 11.7629 13.575C12.0191 14.2375 11.8129 15.55 11.7441 16.0063L11.3816 18.8188L9.37539 29.8375C9.36914 29.8688 9.36289 29.9 9.35664 29.9313L9.34414 30.0125C9.20039 31.0188 9.93789 32 11.3441 32C12.5691 32 13.1129 31.1563 13.3441 30.0063C13.3441 30.0063 15.0941 20.1563 15.9691 20.1563C16.8441 20.1563 18.6441 30.0063 18.6441 30.0063C18.8816 31.1563 19.4191 32 20.6441 32C22.0504 32 22.7941 31.0188 22.6441 30.0063C22.6316 29.9188 22.6129 29.8375 22.5941 29.75L20.5629 18.8313L20.2004 16.0188C19.9379 14.3813 20.1504 13.8375 20.2191 13.7125C20.2191 13.7125 20.2254 13.7063 20.2254 13.7C20.2941 13.575 20.6004 13.2938 21.3191 13.025L26.9004 11.075C26.9316 11.0688 26.9691 11.0563 27.0004 11.0438C28.0004 10.6688 29.0004 10.15 29.0004 9.05C29.0004 7.95 28.1879 6.7375 27.0004 7.05Z"
                        fill="#3AA1FF"
                      />
                    </svg>
                    <h4 className="tbk-text-main md:tbk-text-cta tbk-mb-0 tbk-mt-0 tbk-uppercase tbk-text-primary lg:tbk-mb-2 lg:tbk-mt-2">
                      More accessible exercise
                    </h4>
                  </div>
                  <div className="tbk-text-main tbk-text-primary">
                    <span className="tbk-text-blue-grey">Space needed:</span>{" "}
                    10x10 ft
                  </div>
                  <div className="tbk-text-main tbk-text-primary">
                    <span className="tbk-text-blue-grey">Duration:</span> 1-HR
                  </div>
                  <div className="tbk-text-main tbk-text-primary">
                    <span className="tbk-text-blue-grey">Location:</span> Your
                    choice
                  </div>
                </div>
              </div>

              <div className="col-md-3 tbk-mb-2 lg:tbk-mb-7">
                <div className="tbk-h-full tbk-rounded-lg tbk-bg-basic-white tbk-p-2 tbk-text-left md:tbk-p-3 lg:tbk-bg-grey-light">
                  <div className="tbk-mb-2 tbk-flex tbk-items-center lg:tbk-mb-0 lg:tbk-block">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="tbk-mr-2 tbk-shrink-0"
                    >
                      <path
                        d="M20.8 16.5625C27.7764 15.2305 27.9984 6.352 27.9999 6.2605C28.0029 6.0595 27.9249 5.866 27.7839 5.7235C27.6429 5.581 27.4509 5.5 27.2499 5.5H22V4.75C22 4.336 21.664 4 21.25 4H10.75C10.336 4 10 4.336 10 4.75V5.5H4.75008C4.54908 5.5 4.35708 5.581 4.21608 5.7235C4.07508 5.866 3.99708 6.0595 4.00008 6.2605C4.00158 6.352 4.22208 15.232 11.2 16.5625C11.8525 17.4385 12.7255 18.136 13.75 18.553V21.2035C13.75 22.747 12.823 24.1105 11.38 24.679L10.465 25.054C10.1845 25.171 10 25.4455 10 25.75V27.25C10 27.664 10.336 28 10.75 28H21.25C21.664 28 22 27.664 22 27.25V25.75C22 25.4455 21.8155 25.171 21.5335 25.0555L20.6095 24.6775C19.1755 24.1105 18.25 22.7485 18.25 21.205V18.5545C19.276 18.136 20.149 17.4385 20.8 16.5625ZM22 13V7H26.4444C26.2524 8.8015 25.3974 13.2775 21.709 14.752C21.88 14.194 22 13.6135 22 13ZM5.54957 7H10V13C10 13.615 10.12 14.197 10.2925 14.7565C6.55457 13.285 5.72807 8.812 5.54957 7Z"
                        fill="#3AA1FF"
                      />
                    </svg>
                    <h4 className="tbk-text-main md:tbk-text-cta tbk-mb-0 tbk-mt-0 tbk-uppercase tbk-text-primary lg:tbk-mb-2 lg:tbk-mt-2">
                      The best{" "}
                      <FormattedMessage
                        id="trainers"
                        defaultMessage="trainers"
                      />
                    </h4>
                  </div>
                  <div className="tbk-text-main tbk-text-blue-grey">
                    Fully qualified & ensured
                    <br />
                    At least 2 yrs experience
                    <br />
                    Background checked
                  </div>
                </div>
              </div>

              <div className="col-md-6 tbk-col-span-2 tbk-mb-7">
                <div className="tbk-h-full tbk-justify-between tbk-rounded-lg tbk-bg-basic-white tbk-p-3 tbk-text-left lg:tbk-flex lg:tbk-bg-grey-light">
                  <div className="tbk-mb-2 tbk-flex tbk-items-center lg:tbk-mb-0 lg:tbk-items-start">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="tbk-mr-2 tbk-shrink-0"
                    >
                      <g clipPath="url(#clip0_7934_31982)">
                        <path
                          d="M24.9819 10.3223L26.7258 9.30502C22.9527 2.82326 14.8201 0.347097 8.07501 3.62639V1.1448H6.05664V7.19992H12.1118V5.18155H9.53329C15.2426 2.75969 21.8621 4.96243 24.9819 10.3223Z"
                          fill="#3AA1FF"
                        />
                        <path
                          d="M6.56143 26.0474C1.88214 22.2472 0.650554 15.6136 3.65396 10.3869L1.90403 9.37775C-1.52159 15.343 -0.22542 22.9008 4.99214 27.3837H2.52468V29.402H8.5798V23.3469H6.56143V26.0474Z"
                          fill="#3AA1FF"
                        />
                        <path
                          d="M27.7183 13.8283L23.4373 18.1102L24.8643 19.5372L26.58 17.8216C25.907 24.0742 20.6382 28.8195 14.3496 28.8369V30.8553C21.8422 30.8356 28.0578 25.0531 28.6175 17.5814L30.5733 19.5372L32.0003 18.1102L27.7183 13.8283Z"
                          fill="#3AA1FF"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_7934_31982">
                          <rect width="32" height="32" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>

                    <h4 className="tbk-text-main md:tbk-text-cta tbk-mb-0 tbk-mt-0 tbk-flex-grow tbk-uppercase tbk-text-primary lg:tbk-mb-2">
                      Continuous support
                    </h4>
                  </div>
                  <div className="tbk-text-left">
                    <p className="tbk-text-main tbk-whitespace-nowrap tbk-text-primary">
                      <span className="tbk-text-blue-grey">Email:</span>{" "}
                      members@trubeapp.com
                    </p>
                    <p className="tbk-text-main tbk-whitespace-nowrap tbk-text-primary">
                      <span className="tbk-text-blue-grey">Phone:</span>{" "}
                      <PhoneNumberLink
                        className="tbk-text-blue tbk-underline hover:tbk-text-blue-light"
                        region={
                          region /*process.env.REACT_APP_REGION*/ === "GB"
                            ? "GB"
                            : "US_redirect"
                        }
                        usFormat="national"
                      />
                    </p>
                    <p className="tbk-text-main tbk-whitespace-nowrap tbk-text-primary">
                      <span className="tbk-text-blue-grey">Whatsapp: </span>
                      <WhatsAppLink>Click here</WhatsAppLink>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer className="tbk-relative" />
      {clientWidth() < theme.responsive.breakpoints.lg && (
        <div className="tbk-sticky tbk-bottom-0 tbk-z-995 tbk-mt-[-8px] tbk-w-full tbk-rounded-t-lg tbk-bg-basic-white">
          <BookingBox
            trainerFirstName={partnerFirstName}
            onSubmit={handleBookWithMeRequest}
          />
        </div>
      )}
      <ShareModal
        isOpen={isShareModalOpen}
        modalTitle="Share the link"
        modalText={`Share the link to ${partnerFirstName} so that your friend can experience the best fitness and well-being journey like you have.`}
        shareTitle={`Book a session with ${partnerFirstName} | TruBe`}
        shareText="Experience the best fitness and well-being journey!"
        shareLabel={`Link to ${partnerFirstName}'s profile:`}
        shareInput={`https://us.trubeapp.com/partners/${
          partnerSummary && partnerSummary.id
        }`}
        shareUrl={`https://us.trubeapp.com/partners/${
          partnerSummary && partnerSummary.id
        }`}
        copyFeedbackText="The link has been copied!"
        onClose={closeShareModal}
      />
      <Modal
        isOpen={isLocationSelectorModalOpen}
        // onAfterOpen={afterOpenModal}
        onRequestClose={() => closeLocationSelectorModal(true)}
        style={locationSelectorModalCustomStyle}
        contentLabel="Location Selector Modal"
      >
        <button
          onClick={() => closeLocationSelectorModal(true)}
          className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-text-coral"
        >
          <CloseIcon />
        </button>
        <div className="modal-select-location tbk-flex tbk-max-h-full tbk-w-full tbk-grow tbk-flex-col">
          <LocationSelector
            gymsListRef={refCallback}
            bookedPartner={partnerSummary}
            region={region}
            gyms={partnerGyms}
            selected={selectedLocation}
            preserve={false}
            closeTimeoutMS={300}
            onSelect={handleLocationSelect}
            onCloseClick={() => closeLocationSelectorModal(true)}
            showSpecifyLocationInfo={showSpecifyLocationInfo}
            onBackToSearchClick={() => setShowSpecifyLocationInfo(false)}
            onApplyLocationClick={() => closeLocationSelectorModal()}
            onShowAllGymsClick={(val) => setShowAllGyms(val)}
          />
        </div>
      </Modal>
      <TruBeDrawer
        attachRef={panelRef}
        allowDragging={true}
        isOpen={
          isBookingFlowPanelOpen ||
          (clientWidth() < theme.responsive.breakpoints.lg &&
            isReviewsModalOpen)
        }
        containerStyle={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
        }}
        title={isBookingFlowPanelOpen ? BookingFlowPanelHeader : ReviewsHeader}
        titleElementProps={{
          isCompact: false,
          partnerProducts: partnerProducts,
          isOnline,
          partnerSummary: {
            ...partnerSummary,
            partnerRatingRecap: {
              average: partnerSummary ? partnerSummary.rating : 0,
              numberOfRatings: partnerReviewsNumber,
            },
          },
          partnerLoaded,
          onClose: isBookingFlowPanelOpen
            ? closeBookingFlowPanel
            : closeReviewsModal,
        }}
        onFullyOpen={handleFullyOpen}
        onBottomReached={handleBottomReached}
        onClose={
          isBookingFlowPanelOpen ? closeBookingFlowPanel : closeReviewsModal
        }
      >
        {isBookingFlowPanelOpen ? (
          clientWidth() < theme.responsive.breakpoints.lg && (
            <BookingFlowPanel
              ref={bookingFlowPanelRef}
              className=""
              isFullyOpen={isBookingFlowPanelFullyOpen}
              region={
                user ? user.region : region /*process.env.REACT_APP_REGION*/
              }
              sessionType={sessionType}
              bookingStep={bookingStep}
              // userId={user && user.id}
              min={minTime.toDate()}
              max={maxTime.toDate()}
              bookingTime={_bookingTime}
              workout={workoutType}
              workoutValues={partnerProducts}
              partnerSummary={partnerSummary}
              partnerFirstName={partnerSummary && partnerSummary.firstName}
              loadPartnerProfile={loadPartnerProfile}
              partnerLoaded={partnerLoaded}
              partnerAvailability={partnerAvailability}
              partnerAvailabilityLoaded={partnerAvailabilityLoaded}
              clearPartnerAvailabilityLoaded={clearPartnerAvailabilityLoaded}
              onDateTimeSelect={handleBookingTimeChange}
              onWorkoutChange={handleWorkoutChange}
              onClose={closeBookingFlowPanel}
              onCtaClick={handleBookingFlowPopupCtaClick}
              // onSubmit={handleBookWithMeRequest}
            />
          )
        ) : (
          <Reviews
            partnerId={partnerSummary && partnerSummary.id}
            partnerReviews={partnerReviews}
            onLoadMoreReviews={loadReviews}
            // onClose={closeReviewsModal}
          />
        )}
      </TruBeDrawer>

      <Transition
        nodeRef={bookingFlowPopupRef}
        in={
          clientWidth() >= theme.responsive.breakpoints.lg &&
          isBookingFlowPopupOpen /*&& bookedPartner*/
        }
        timeout={300}
        // addEndListener={this.handleTransitionEnd}
      >
        {(status, innerProps) => (
          <BookingFlowPopup
            ref={bookingFlowPopupRef}
            className={`tbk-transition-transform tbk-duration-300 ${
              status === ENTERING || status === EXITED
                ? "tbk-translate-x-full tbk-opacity-5"
                : status === EXITING
                ? "tbk-translate-x-full tbk-opacity-5"
                : "tbk-translate-x-0 tbk-opacity-100"
            }`}
            region={
              user ? user.region : region /*process.env.REACT_APP_REGION*/
            }
            sessionType={sessionType}
            bookingStep={bookingStep}
            // userId={user && user.id}
            min={minTime.toDate()}
            max={maxTime.toDate()}
            bookingTime={_bookingTime}
            workout={workoutType}
            workoutValues={partnerProducts}
            partnerSummary={partnerSummary}
            loadPartnerProfile={loadPartnerProfile}
            partnerLoaded={partnerLoaded}
            partnerAvailability={partnerAvailability}
            partnerAvailabilityLoaded={partnerAvailabilityLoaded}
            clearPartnerAvailabilityLoaded={clearPartnerAvailabilityLoaded}
            onDateTimeSelect={handleBookingTimeChange}
            onWorkoutChange={handleWorkoutChange}
            onClose={closeBookingFlowPopup}
            onCtaClick={handleBookingFlowPopupCtaClick}
          />
        )}
      </Transition>
      {partnerSummary && clientWidth() >= theme.responsive.breakpoints.lg && (
        <Modal
          appElement={/*__CLIENT__ && */ document.getElementById("#app")}
          isOpen={isReviewsModalOpen}
          onRequestClose={closeReviewsModal}
          style={reviewsModalCustomStyle}
          contentLabel="Reviews modal"
        >
          <div className="tbk-flex tbk-h-full tbk-flex-col tbk-rounded-lg tbk-bg-basic-white">
            <div className="tbk-h-full tbk-grow">
              <ReviewsHeader
                isCompact={false}
                partnerSummary={{
                  ...partnerSummary,
                  partnerRatingRecap: {
                    average: partnerSummary ? partnerSummary.rating : 0,
                    numberOfRatings: partnerReviewsNumber,
                  },
                }}
                partnerProducts={partnerProducts}
                onClose={closeReviewsModal}
              />
              <Reviews
                partnerId={partnerSummary && partnerSummary.id}
                partnerReviews={partnerReviews}
                onLoadMoreReviews={loadReviews}
                // onClose={closeReviewsModal}
              />
            </div>
          </div>
        </Modal>
      )}
      <Modal
        appElement={document.getElementById("#app")}
        isOpen={isSignUpModalOpen}
        // onAfterOpen={this.afterOpenModal}
        onRequestClose={closeSignUpModal}
        style={defaultCustomStyle}
        contentLabel="Sign Up Modal"
      >
        <button
          onClick={closeSignUpModal}
          className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-text-coral"
        >
          <CloseIcon />
        </button>
        <SignUpForm
          smallButtons
          //withReferral={true}
          returnTo={returnUrl}
          onNext={handleNext}
          onSignIn={() => {
            closeSignUpModal();
            continueBookingFlow();
          }}
          // onSignup={}
          // onLogin={}
          // onPasswordReset={}
          // isUserAuthenticating={}
          // loginWithFacebook={}
          // loginWithApple={}
          // register={}
        />
      </Modal>
      <Modal
        isOpen={isGalleryCarouselModalOpen}
        // onAfterOpen={this.afterOpenModal}
        onRequestClose={closeGalleryCarouselModal.bind(null, true)}
        style={modalStyles.galleryCarouselDefault}
        contentLabel="Gallery Carousel"
      >
        {/*<Link role='button' onClick={this.closeDateTimeSelectorModal}><Icon name='close' className='tb-pd-0' width='32px' height='32px' /></Link>*/}
        <div
          role="button"
          className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-p-1"
          onClick={closeGalleryCarouselModal}
        >
          <CloseIcon className="tbk-p-0 tbk-text-basic-white" />
        </div>
        <div className="modal-gallery-carousel u-d-flex_ u-flex-column_ tbk-h-full">
          <MediaBlock
            media={partnerLoaded && partnerMedia}
            initialSlide={galleryInitialSlide}
          />
        </div>
      </Modal>
    </>
  );
}

const partnerAvailable = (partnerId, availablePartners) => {
  return !!(
    availablePartners &&
    availablePartners.length &&
    availablePartners.find((val) => val.id === partnerId)
  );
};

const makeContinueBookingFlowUrl = (
  location,
  paramName = CONTINUE_BOOKING_FLOW_PARAM_NAME,
  paramValue
) => {
  const query = new URLSearchParams(location.search);
  let queryStr = "";

  if (query.has(CONTINUE_BOOKING_FLOW_PARAM_NAME)) {
    console.warn("Query param duplication!", CONTINUE_BOOKING_FLOW_PARAM_NAME);
  } else {
    queryStr = `${paramName}=${paramValue}`;
  }

  return `${location.pathname}${
    location.search ? `${location.search}&${queryStr}` : `?${queryStr}`
  }`;
};
